<template>
  <HrPageLayout
    title="My Leave History"
    subtitle="My Leave"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/employee-dashboard' },
      { label: 'My Leave' }
    ]"
  >
          <!-- Statistics Cards -->
          <div class="row">
            <div class="col-xl-3 col-md-6">
              <div class="card card-stats">
                <div class="card-body">
                  <div class="d-flex justify-content-between">
                    <div>
                      <h6 class="text-muted text-uppercase mb-2">Total Requests</h6>
                      <h3 class="mb-0">{{ statistics.total }}</h3>
                    </div>
                    <div class="avatar-sm">
                      <span class="avatar-title bg-primary-subtle text-primary rounded-3">
                        <i class="mdi mdi-file-document font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-stats">
                <div class="card-body">
                  <div class="d-flex justify-content-between">
                    <div>
                      <h6 class="text-muted text-uppercase mb-2">Pending</h6>
                      <h3 class="mb-0">{{ statistics.pending }}</h3>
                    </div>
                    <div class="avatar-sm">
                      <span class="avatar-title bg-warning-subtle text-warning rounded-3">
                        <i class="mdi mdi-clock-outline font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-stats">
                <div class="card-body">
                  <div class="d-flex justify-content-between">
                    <div>
                      <h6 class="text-muted text-uppercase mb-2">Approved</h6>
                      <h3 class="mb-0">{{ statistics.approved }}</h3>
                    </div>
                    <div class="avatar-sm">
                      <span class="avatar-title bg-success-subtle text-success rounded-3">
                        <i class="mdi mdi-check-circle font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-stats">
                <div class="card-body">
                  <div class="d-flex justify-content-between">
                    <div>
                      <h6 class="text-muted text-uppercase mb-2">Days Taken</h6>
                      <h3 class="mb-0">{{ statistics.totalDaysApproved }}</h3>
                    </div>
                    <div class="avatar-sm">
                      <span class="avatar-title bg-info-subtle text-info rounded-3">
                        <i class="mdi mdi-calendar-check font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Filter and Actions -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-md-3">
                      <label class="form-label">Filter by Status</label>
                      <select v-model="filters.status" @change="loadLeaveHistory" class="form-select">
                        <option value="">All Status</option>
                        <option value="PENDING">Pending</option>
                        <option value="APPROVED">Approved</option>
                        <option value="REJECTED">Rejected</option>
                        <option value="CANCELLED">Cancelled</option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Filter by Year</label>
                      <select v-model="filters.year" @change="loadLeaveHistory" class="form-select">
                        <option :value="currentYear">{{ currentYear }}</option>
                        <option :value="currentYear - 1">{{ currentYear - 1 }}</option>
                        <option :value="currentYear - 2">{{ currentYear - 2 }}</option>
                      </select>
                    </div>
                    <div class="col-md-6 text-md-end mt-3 mt-md-0">
                      <label class="form-label d-block">&nbsp;</label>
                      <button @click="gotTo('employee-leave-apply')" class="btn btn-primary">
                        <i class="mdi mdi-plus me-1"></i>
                        Apply for Leave
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading leave history...</p>
          </div>

          <!-- Leave Requests List -->
          <div v-else class="row">
            <div class="col-12">
              <div v-if="leaveRequests.length === 0" class="card">
                <div class="card-body text-center py-5">
                  <div class="avatar-lg mx-auto mb-4">
                    <div class="avatar-title bg-light text-muted rounded-circle">
                      <i class="mdi mdi-file-document-outline font-size-48"></i>
                    </div>
                  </div>
                  <h5>No leave requests found</h5>
                  <p class="text-muted">You haven't applied for any leave yet.</p>
                  <button @click="gotTo('employee-leave-apply')" class="btn btn-primary mt-3">
                    <i class="mdi mdi-plus me-1"></i>
                    Apply for Leave
                  </button>
                </div>
              </div>

              <div v-else>
                <div v-for="request in leaveRequests" :key="request.id" class="card mb-3">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-md-8">
                        <div class="d-flex align-items-start">
                          <div class="flex-shrink-0">
                            <div class="avatar-sm">
                              <span :class="getLeaveIconClass(request.status)">
                                <i :class="getLeaveIcon(request.status)"></i>
                              </span>
                            </div>
                          </div>
                          <div class="flex-grow-1 ms-3">
                            <h5 class="mb-1">{{ request.leaveTypeName }}</h5>
                            <p class="text-muted mb-2">
                              <i class="mdi mdi-calendar-range me-1"></i>
                              {{ formatDate(request.startDate) }} to {{ formatDate(request.endDate) }}
                              <span class="badge bg-light text-dark ms-2">{{ request.totalDays }} day(s)</span>
                            </p>
                            <p class="mb-2">
                              <strong>Reason:</strong> {{ request.reason }}
                            </p>
                            <div class="d-flex flex-wrap gap-2 align-items-center">
                              <span :class="getStatusBadgeClass(request.status)">
                                {{ request.status }}
                              </span>
                              <small class="text-muted">
                                <i class="mdi mdi-clock-outline me-1"></i>
                                Applied on {{ formatDate(request.createdAt) }}
                              </small>
                              <span v-if="request.approvedBy" class="text-success small">
                                <i class="mdi mdi-account-check me-1"></i>
                                Approved by {{ request.approvedBy }}
                              </span>
                              <span v-if="request.rejectedBy" class="text-danger small">
                                <i class="mdi mdi-account-cancel me-1"></i>
                                Rejected by {{ request.rejectedBy }}
                              </span>
                            </div>
                            <div v-if="request.rejectionReason" class="alert alert-danger mt-2 mb-0">
                              <small>
                                <strong>Reason:</strong> {{ request.rejectionReason }}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4 text-md-end mt-3 mt-md-0">
                        <div class="d-flex flex-column gap-2">
                          <button v-if="request.status === 'PENDING'"
                                  @click="openCancelModal(request)"
                                  class="btn btn-sm btn-outline-danger">
                            <i class="mdi mdi-close-circle me-1"></i>
                            Cancel Request
                          </button>
                          <button v-if="request.documents"
                                  @click="viewDocument(request.documents)"
                                  class="btn btn-sm btn-outline-info">
                            <i class="mdi mdi-file-document me-1"></i>
                            View Document
                          </button>
                          <button @click="viewDetails(request)"
                                  class="btn btn-sm btn-outline-primary">
                            <i class="mdi mdi-eye me-1"></i>
                            View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Pagination -->
                <div v-if="pagination.totalPages > 1" class="card">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        Showing {{ ((pagination.currentPage - 1) * pagination.limit) + 1 }}
                        to {{ Math.min(pagination.currentPage * pagination.limit, pagination.totalRecords) }}
                        of {{ pagination.totalRecords }} records
                      </div>
                      <nav>
                        <ul class="pagination mb-0">
                          <li class="page-item" :class="{ disabled: pagination.currentPage === 1 }">
                            <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage - 1)">
                              <i class="mdi mdi-chevron-left"></i>
                            </a>
                          </li>
                          <li v-for="page in visiblePages"
                              :key="page"
                              class="page-item"
                              :class="{ active: page === pagination.currentPage }">
                            <a class="page-link" href="javascript:void(0)" @click="changePage(page)">
                              {{ page }}
                            </a>
                          </li>
                          <li class="page-item" :class="{ disabled: pagination.currentPage === pagination.totalPages }">
                            <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage + 1)">
                              <i class="mdi mdi-chevron-right"></i>
                            </a>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

    <Teleport to="body">
    <!-- Cancel Modal -->
    <div class="modal fade" id="cancelModal" tabindex="-1" ref="cancelModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Cancel Leave Request</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to cancel this leave request?</p>
            <div v-if="selectedRequest" class="alert alert-info">
              <strong>{{ selectedRequest.leaveTypeName }}</strong><br>
              {{ formatDate(selectedRequest.startDate) }} to {{ formatDate(selectedRequest.endDate) }}
              ({{ selectedRequest.totalDays }} days)
            </div>
            <div class="mb-3">
              <label class="form-label">Reason for Cancellation (Optional)</label>
              <textarea v-model="cancelReason" class="form-control" rows="3"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            <button type="button" @click="confirmCancel" class="btn btn-danger" :disabled="cancelling">
              <span v-if="cancelling">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Cancelling...
              </span>
              <span v-else>
                <i class="mdi mdi-close-circle me-1"></i>
                Yes, Cancel Request
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Details Modal -->
    <div class="modal fade" id="detailsModal" tabindex="-1" ref="detailsModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="mdi mdi-file-document-outline me-2"></i>
              Leave Request Details
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" v-if="selectedRequest">
            <!-- Status Banner -->
            <div class="alert" :class="getDetailStatusClass(selectedRequest.status)">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h5 class="alert-heading mb-1">
                    <i :class="getLeaveIcon(selectedRequest.status)"></i>
                    Status: {{ selectedRequest.status }}
                  </h5>
                  <p class="mb-0" v-if="selectedRequest.status === 'PENDING'">
                    Your leave request is pending approval
                  </p>
                  <p class="mb-0" v-else-if="selectedRequest.status === 'APPROVED' || selectedRequest.status === 'Approved'">
                    Your leave request has been approved
                  </p>
                  <p class="mb-0" v-else-if="selectedRequest.status === 'REJECTED' || selectedRequest.status === 'Rejected'">
                    Your leave request has been rejected
                  </p>
                </div>
                <div class="text-end">
                  <small class="d-block">Request ID</small>
                  <strong>#{{ selectedRequest.id }}</strong>
                </div>
              </div>
            </div>

            <!-- Leave Information -->
            <div class="row mb-4">
              <div class="col-12">
                <h6 class="text-primary mb-3">
                  <i class="mdi mdi-information-outline me-1"></i>
                  Leave Information
                </h6>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">Leave Type</label>
                <p class="mb-0 fw-semibold">{{ selectedRequest.leaveTypeName }}</p>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">Total Days</label>
                <p class="mb-0 fw-semibold">{{ selectedRequest.totalDays }} day(s)</p>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">Start Date</label>
                <p class="mb-0 fw-semibold">
                  <i class="mdi mdi-calendar-start me-1 text-success"></i>
                  {{ formatDate(selectedRequest.startDate) }}
                </p>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">End Date</label>
                <p class="mb-0 fw-semibold">
                  <i class="mdi mdi-calendar-end me-1 text-danger"></i>
                  {{ formatDate(selectedRequest.endDate) }}
                </p>
              </div>
              <div class="col-12 mb-3">
                <label class="text-muted small">Reason for Leave</label>
                <p class="mb-0">{{ selectedRequest.reason }}</p>
              </div>
            </div>

            <!-- Contact Information -->
            <div class="row mb-4" v-if="selectedRequest.contactNumber || selectedRequest.alternateContact">
              <div class="col-12">
                <h6 class="text-primary mb-3">
                  <i class="mdi mdi-phone me-1"></i>
                  Contact Information
                </h6>
              </div>
              <div class="col-md-6 mb-3" v-if="selectedRequest.contactNumber">
                <label class="text-muted small">Contact Number</label>
                <p class="mb-0">
                  <i class="mdi mdi-phone me-1"></i>
                  {{ selectedRequest.contactNumber }}
                </p>
              </div>
              <div class="col-md-6 mb-3" v-if="selectedRequest.alternateContact">
                <label class="text-muted small">Alternate Contact</label>
                <p class="mb-0">
                  <i class="mdi mdi-account-circle me-1"></i>
                  {{ selectedRequest.alternateContact }}
                </p>
              </div>
            </div>

            <!-- Department & Manager -->
            <div class="row mb-4">
              <div class="col-12">
                <h6 class="text-primary mb-3">
                  <i class="mdi mdi-office-building me-1"></i>
                  Department & Approval
                </h6>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">Department</label>
                <p class="mb-0">{{ selectedRequest.departmentName || 'N/A' }}</p>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">Manager</label>
                <p class="mb-0">{{ selectedRequest.managerName || 'N/A' }}</p>
              </div>
              <div class="col-md-6 mb-3" v-if="selectedRequest.approvedBy">
                <label class="text-muted small">Approved By</label>
                <p class="mb-0 text-success">
                  <i class="mdi mdi-account-check me-1"></i>
                  {{ selectedRequest.approvedBy }}
                </p>
                <small class="text-muted" v-if="selectedRequest.approvalDate">
                  on {{ formatDate(selectedRequest.approvalDate) }}
                </small>
              </div>
              <div class="col-md-6 mb-3" v-if="selectedRequest.rejectedBy">
                <label class="text-muted small">Rejected By</label>
                <p class="mb-0 text-danger">
                  <i class="mdi mdi-account-cancel me-1"></i>
                  {{ selectedRequest.rejectedBy }}
                </p>
                <small class="text-muted" v-if="selectedRequest.rejectionDate">
                  on {{ formatDate(selectedRequest.rejectionDate) }}
                </small>
              </div>
            </div>

            <!-- Rejection Reason -->
            <div class="row mb-4" v-if="selectedRequest.rejectionReason">
              <div class="col-12">
                <div class="alert alert-danger">
                  <h6 class="alert-heading">
                    <i class="mdi mdi-alert-circle me-1"></i>
                    Rejection Reason
                  </h6>
                  <p class="mb-0">{{ selectedRequest.rejectionReason }}</p>
                </div>
              </div>
            </div>

            <!-- Supporting Document -->
            <div class="row mb-4" v-if="selectedRequest.documents">
              <div class="col-12">
                <h6 class="text-primary mb-3">
                  <i class="mdi mdi-file-document me-1"></i>
                  Supporting Document
                </h6>
              </div>
              <div class="col-12">
                <div class="border rounded p-3">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <i class="mdi mdi-file-pdf font-size-24 text-danger me-2"></i>
                      <span>Supporting Document</span>
                    </div>
                    <button @click="viewDocument(selectedRequest.documents)" class="btn btn-sm btn-primary">
                      <i class="mdi mdi-download me-1"></i>
                      View / Download
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Timeline -->
            <div class="row">
              <div class="col-12">
                <h6 class="text-primary mb-3">
                  <i class="mdi mdi-timeline me-1"></i>
                  Timeline
                </h6>
              </div>
              <div class="col-12">
                <ul class="timeline">
                  <li class="timeline-item">
                    <div class="timeline-badge bg-primary">
                      <i class="mdi mdi-plus"></i>
                    </div>
                    <div class="timeline-panel">
                      <div class="timeline-heading">
                        <h6 class="timeline-title">Request Submitted</h6>
                      </div>
                      <div class="timeline-body">
                        <p class="text-muted mb-0">{{ formatDateTime(selectedRequest.createdAt) }}</p>
                      </div>
                    </div>
                  </li>
                  <li class="timeline-item" v-if="selectedRequest.approvalDate">
                    <div class="timeline-badge bg-success">
                      <i class="mdi mdi-check"></i>
                    </div>
                    <div class="timeline-panel">
                      <div class="timeline-heading">
                        <h6 class="timeline-title">Request Approved</h6>
                      </div>
                      <div class="timeline-body">
                        <p class="text-muted mb-0">
                          {{ formatDateTime(selectedRequest.approvalDate) }}
                          <br>
                          <small>by {{ selectedRequest.approvedBy }}</small>
                        </p>
                      </div>
                    </div>
                  </li>
                  <li class="timeline-item" v-if="selectedRequest.rejectionDate">
                    <div class="timeline-badge bg-danger">
                      <i class="mdi mdi-close"></i>
                    </div>
                    <div class="timeline-panel">
                      <div class="timeline-heading">
                        <h6 class="timeline-title">Request Rejected</h6>
                      </div>
                      <div class="timeline-body">
                        <p class="text-muted mb-0">
                          {{ formatDateTime(selectedRequest.rejectionDate) }}
                          <br>
                          <small>by {{ selectedRequest.rejectedBy }}</small>
                        </p>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              <i class="mdi mdi-close me-1"></i>
              Close
            </button>
            <button v-if="selectedRequest && selectedRequest.status === 'PENDING'"
                    @click="openCancelFromDetails"
                    class="btn btn-danger">
              <i class="mdi mdi-close-circle me-1"></i>
              Cancel Request
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>

  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, uploads } from "@/api";
import { Modal } from "bootstrap";

export default {
  name: "EmployeeLeaveHistory",
  components: { HrPageLayout },

  data() {
    return {
      loading: false,
      cancelling: false,
      leaveRequests: [],
      statistics: {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        cancelled: 0,
        totalDaysApproved: 0
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        limit: 10
      },
      filters: {
        status: '',
        year: new Date().getFullYear()
      },
      currentYear: new Date().getFullYear(),
      selectedRequest: null,
      cancelReason: '',
      cancelModalInstance: null,
      detailsModalInstance: null
    };
  },

  computed: {
    visiblePages() {
      const pages = [];
      const total = this.pagination.totalPages;
      const current = this.pagination.currentPage;

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        }
      }

      return pages;
    }
  },

  mounted() {
    this.loadLeaveHistory();
    this.initCancelModal();
    this.initDetailsModal();
  },

  methods: {
    async loadLeaveHistory() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeLeaveHistory");
        data.append("status", this.filters.status);
        data.append("year", this.filters.year);
        data.append("page", this.pagination.currentPage);
        data.append("limit", this.pagination.limit);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.leaveRequests = res.data.data.leaveRequests;
          this.statistics = res.data.data.statistics;
          this.pagination = res.data.data.pagination;
        } else {
          throw new Error(res.data?.message || 'Failed to load leave history');
        }
      } catch (error) {
        this.$toast?.error("Failed to load leave history: " + error.message) ||
        console.error("Failed to load leave history:", error);
      } finally {
        this.loading = false;
      }
    },

    initCancelModal() {
      const modalElement = this.$refs.cancelModal;
      if (modalElement) {
        this.cancelModalInstance = Modal.getOrCreateInstance(modalElement);
      }
    },

    openCancelModal(request) {
      this.selectedRequest = request;
      this.cancelReason = '';
      if (this.cancelModalInstance) {
        this.cancelModalInstance.show();
      }
    },

    async confirmCancel() {
      if (!this.selectedRequest) return;

      this.cancelling = true;

      try {
        const data = new FormData();
        data.append("function", "cancelLeaveRequest");
        data.append("leaveRequestId", this.selectedRequest.id);
        data.append("cancelReason", this.cancelReason || 'Cancelled by employee');

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.$toast?.success("Leave request cancelled successfully") ||
          alert("Leave request cancelled successfully");

          if (this.cancelModalInstance) {
            this.cancelModalInstance.hide();
          }

          await this.loadLeaveHistory();
        } else {
          throw new Error(res.data?.message || 'Failed to cancel leave request');
        }
      } catch (error) {
        this.$toast?.error("Error: " + error.message) ||
        alert("Error: " + error.message);
      } finally {
        this.cancelling = false;
      }
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.pagination.currentPage) {
        return;
      }
      this.pagination.currentPage = page;
      this.loadLeaveHistory();
    },

    viewDocument(documentPath) {
      if (documentPath) {
        window.open(uploads + documentPath, '_blank');
      }
    },

    viewDetails(request) {
      this.selectedRequest = request;
      if (this.detailsModalInstance) {
        this.detailsModalInstance.show();
      }
    },

    openCancelFromDetails() {
      // Close details modal first
      if (this.detailsModalInstance) {
        this.detailsModalInstance.hide();
      }
      // Small delay to allow details modal to close
      setTimeout(() => {
        if (this.cancelModalInstance) {
          this.cancelModalInstance.show();
        }
      }, 300);
    },

    initDetailsModal() {
      const modalElement = this.$refs.detailsModal;
      if (modalElement) {
        this.detailsModalInstance = Modal.getOrCreateInstance(modalElement);
      }
    },

    formatDateTime(datetime) {
      if (!datetime) return 'N/A';
      const d = new Date(datetime);
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return d.toLocaleDateString('en-US', options);
    },

    getDetailStatusClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'approved') return 'alert-success';
      if (statusLower === 'rejected') return 'alert-danger';
      if (statusLower === 'pending') return 'alert-warning';
      if (statusLower === 'cancelled') return 'alert-secondary';
      return 'alert-info';
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    getStatusBadgeClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'approved') return 'badge bg-success';
      if (statusLower === 'rejected') return 'badge bg-danger';
      if (statusLower === 'pending') return 'badge bg-warning';
      if (statusLower === 'cancelled') return 'badge bg-secondary';
      return 'badge bg-secondary';
    },

    getLeaveIcon(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'approved') return 'mdi mdi-check-circle font-size-20';
      if (statusLower === 'rejected') return 'mdi mdi-close-circle font-size-20';
      if (statusLower === 'pending') return 'mdi mdi-clock-outline font-size-20';
      if (statusLower === 'cancelled') return 'mdi mdi-cancel font-size-20';
      return 'mdi mdi-file-document font-size-20';
    },

    getLeaveIconClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'approved') return 'avatar-title bg-success-subtle text-success rounded';
      if (statusLower === 'rejected') return 'avatar-title bg-danger-subtle text-danger rounded';
      if (statusLower === 'pending') return 'avatar-title bg-warning-subtle text-warning rounded';
      if (statusLower === 'cancelled') return 'avatar-title bg-secondary-subtle text-secondary rounded';
      return 'avatar-title bg-light text-muted rounded';
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      if (typeof route === "string" && route.startsWith("/")) {
        this.$router.push(route);
      } else {
        this.$router.push({ name: route });
      }
    }
  }
};
</script>

<style scoped>
.card-stats {
  transition: all 0.3s ease;
}

.card-stats:hover {
  transform: translateY(-3px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.avatar-sm {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-lg {
  width: 72px;
  height: 72px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
}

.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-danger-subtle {
  background-color: rgba(220, 53, 69, 0.1);
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-info-subtle {
  background-color: rgba(23, 162, 184, 0.1);
}

.bg-primary-subtle {
  background-color: rgba(91, 115, 232, 0.1);
}

.bg-secondary-subtle {
  background-color: rgba(108, 117, 125, 0.1);
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
}

.pagination .page-link {
  color: #059669;
}

.pagination .page-item.active .page-link {
  background-color: #059669;
  border-color: #059669;
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .text-md-end {
    text-align: left !important;
  }
}

/* Timeline Styles */
.timeline {
  list-style: none;
  padding: 20px 0;
  position: relative;
}

.timeline:before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #dee2e6;
  left: 30px;
}

.timeline-item {
  margin-bottom: 20px;
  position: relative;
  padding-left: 60px;
}

.timeline-badge {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  text-align: center;
  position: absolute;
  left: 10px;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  border: 3px solid white;
  box-shadow: 0 0 0 2px #dee2e6;
}

.timeline-panel {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border-left: 3px solid #059669;
}

.timeline-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.timeline-body {
  margin-top: 8px;
}
</style>